import { Portfolio } from '../types';

export const portfolioCategories = [
  'All',
  'AI',
  'SaaS',
  'Agency',
  'Real Estate',
  'eCommerce',
  'Shipping',
  'Social',
  'Music',
  'Crypto',
  'Sustainability',
];

/** A portfolio opens a case-study sheet only when it has something to show. */
export const hasCaseStudy = (portfolio: Portfolio): boolean =>
  !!portfolio.article || !!portfolio.description;

export const portfolios: Portfolio[] = [
  {
    image: '/portfolios/design-portfolio/portfolio-01.webp',
    mobileImageLink: '/portfolios/design-portfolio/portfolio-mob-01.webp',
    title: 'LegalFacts',
    subtitle: 'AI Attorney Matching Platform',
    alt: 'LegalFacts Portfolio',
    url: '',
    categories: ['All', 'Dark', 'AI', 'SaaS', 'Social'],
    description:
      'I designed and built LegalFacts to replace directory-style attorney search with a guided match. I created a natural-language intake field where users type or speak their legal situation, then data-dense attorney cards comparing success rate, experience, cases won and per-specialisation win ratios. I delivered the dark-theme design system and implemented the responsive front end. People now compare lawyers on evidence instead of advertising, and the shared component library kept every new page consistent and fast to ship.',
    skills: [
      'UI/UX Design',
      'Web Design',
      'Design System',
      'Figma',
      'Prototyping',
      'Interaction Design',
      'Dark Mode UI',
      'Information Design',
      'Landing Page Design',
      'Responsive Web Design',
      'React',
      'Tailwind CSS',
      'HTML5 / CSS3 / JavaScript',
      'SaaS Product Design',
    ],
    article: (
      <>
        <img
          src="/portfolios/design-portfolio/legalfacts/01.webp"
          alt="LegalFacts Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Hiring a lawyer usually starts with a directory — a page of paid
          listings, star ratings and slogans that say nothing about whether any
          of these people actually win cases like yours. LegalFacts starts
          somewhere else. People describe what happened in their own words,
          typing it out or speaking it into the recorder, and the platform pairs
          them with the attorneys most likely to win their specific case.
        </p>
        <p>
          The homepage puts that whole promise on one screen. A single
          plain-language field sits on the left, and the attorney fact cards sit
          immediately beside it — overall success rate, years of experience,
          cases won — visible before anyone has signed up or paid for anything.
        </p>
        <img
          src="/portfolios/design-portfolio/legalfacts/02.webp"
          alt="LegalFacts Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Submitting a case turns a paragraph of plain English into a structured
          brief. An AI-generated Case Summary restates the incident and lifts
          out the details that drive the match — case location, date of event,
          practice area and sub-practice area — with an Edit Details control so
          the client can correct the machine’s reading of their own story before
          it counts.
        </p>
        <p>
          Results then arrive as a 1st, 2nd and 3rd Choice instead of an endless
          list. Each one opens with a plain-English reason why that attorney
          fits, then backs it with evidence: overall success rate, experience,
          cases won, win ratios broken down per specialisation, education,
          client ratings across communication, negotiation, ethics, transparency
          and availability, pro bono hours and bar standing. Booking a
          consultation is one button on the card.
        </p>
        <img
          src="/portfolios/design-portfolio/legalfacts/03.webp"
          alt="LegalFacts Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The Attorneys directory carries the same idea across the full network.
          Practice-area tabs — Accidents &amp; Injuries, Divorce &amp; Family,
          Criminal, Employment, Estate Planning &amp; Insurance and Business —
          filter a grid where every card leads with the success rate rather than
          a headshot and a tagline. Cards expand in place into the full fact
          panel, so people can compare like for like without losing their place
          in the results.
        </p>
        <img
          src="/portfolios/design-portfolio/legalfacts/04.webp"
          alt="LegalFacts Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The attorney profile is the densest screen in the product, and the one
          that had to stay readable under the most pressure. A verified
          identity, direct actions for instant consultation, call and message,
          and then the evidence: testimonials, notable verdicts, awards, service
          area, client reviews, the wider legal team and a plain statement of
          fees. The fact panel sits in the right rail alongside the written
          story, so the numbers stay next to the narrative rather than buried
          under it.
        </p>
        <img
          src="/portfolios/design-portfolio/legalfacts/05.webp"
          alt="LegalFacts Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The design system runs on a single red, #ED3327, over a steel-teal
          surface of #222831 and #393E46, with #EEEEEE carrying the text. Red is
          rationed deliberately — it marks actions, the active practice area and
          the reason a match was made, and nothing else. Everything that counts
          as evidence, from percentages to win ratios to client ratings, stays
          in neutral type, so the interface never looks like it is selling the
          numbers it is reporting.
        </p>
        <img
          src="/portfolios/design-portfolio/legalfacts/06.webp"
          alt="LegalFacts Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Inter carries the whole product, from twelve-pixel labels up to the
          forty-eight-pixel display sizes. It was chosen for its numerals: the
          entire proposition rests on figures like 97%, 129/140 and 63/71 being
          read quickly and compared across three cards at once, and Inter’s
          evenly weighted digits keep those columns aligned and legible even at
          the smallest sizes in the fact panel.
        </p>
        <img
          src="/portfolios/design-portfolio/legalfacts/07.webp"
          alt="LegalFacts Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg border border-gray-200"
        />
        <p>
          End to end, LegalFacts turns a stressful and opaque decision into a
          comparison people can actually make. The shared component library —
          fact card, profile card, practice-area tabs and the consultation
          modals — keeps every page consistent and made each new screen fast to
          ship.
        </p>
      </>
    ),
  },
  {
    image: '/portfolios/design-portfolio/portfolio-02.webp',
    mobileImageLink: '/portfolios/design-portfolio/portfolio-mob-02.webp',
    title: 'Shipfro',
    subtitle: 'Multi-Courier Shipping Platform',
    alt: 'Shipfro Portfolio',
    url: '',
    categories: ['All', 'Light', 'Shipping', 'SaaS'],
    description:
      'I designed the booking experience for Shipfro, a platform that lets people compare and book multiple couriers — TCS, Leopards and others — in one place. I built a five-step quote builder (collection city, delivery city, package type, weight, box size) straight into the hero with domestic and international tabs, plus tracking, services and blog pages. I delivered the red-on-white brand system as a reusable component library and implemented the responsive front end. Quoting a shipment collapsed from several courier sites into one form above the fold.',
    skills: [
      'UI/UX Design',
      'Web Design',
      'Design System',
      'Figma',
      'User Flows',
      'Form & Checkout UX',
      'Landing Page Design',
      'Responsive Web Design',
      'React',
      'Tailwind CSS',
      'HTML5 / CSS3 / JavaScript',
      'Logistics / Shipping UX',
      'SaaS Product Design',
    ],
    article: (
      <>
        <img
          src="/portfolios/design-portfolio/shipfro/01.webp"
          alt="Shipfro Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Sending a parcel in Pakistan normally means opening three courier
          sites, entering the same five details into three different forms, and
          keeping the prices in your head long enough to compare them. Shipfro
          collapses that into one screen. The headline states the whole
          proposition plainly — one platform, multiple courier choices — and the
          quote builder sits directly beneath it, half overlapping the hero, so
          the tool is visible before a single scroll.
        </p>
        <p>
          The form asks for exactly what a rate depends on and nothing else:
          collection city, delivery city, package type as box, envelope or
          flyer, weight in kilograms, and a box size chosen from tiny, small,
          medium and large with the dimensions printed on each option. Putting
          the measurements on the buttons removes the one step that sends people
          away to measure something, and a Domestic tab sits beside an
          International one marked Coming Soon — an honest way to show the
          roadmap without a dead link.
        </p>
        <img
          src="/portfolios/design-portfolio/shipfro/02.webp"
          alt="Shipfro Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Results arrive as a comparison table rather than a list of offers, and
          the columns are chosen to settle the argument in one pass: courier,
          service level with its delivery window, packaging, whether pickup is
          available, whether a printer is required, accepted payment options,
          and the price. Each price shows the Shipfro rate with the standard
          rate struck through beside it and the saving spelled out, so the
          benefit of booking here rather than direct is stated rather than
          implied. Book sits at the end of every row.
        </p>
        <p>
          Printer required and pickup available look like small columns and are
          the most practically important ones on the page. They decide whether a
          shipment is possible this afternoon for someone without a printer at
          home, and no courier site surfaces them at comparison time. Small
          question-mark tooltips on those headers explain the terms without
          padding the table with prose.
        </p>
        <img
          src="/portfolios/design-portfolio/shipfro/03.webp"
          alt="Shipfro Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Below the tool, the page argues the case for the model itself. Four
          service cards cover effortless deliveries, reduced cost, worldwide
          deliveries and reliable partners, and a numbered band laid over a
          delivery-van photograph walks through the four steps end to end — get
          a quote, select a courier and place the order, follow the delivery
          instructions, ship the parcel. For a service that sits between the
          customer and a courier they already know, showing where the handover
          happens is what makes the arrangement feel safe.
        </p>
        <img
          src="/portfolios/design-portfolio/shipfro/04.webp"
          alt="Shipfro Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Proof then does the closing. Eight testimonials sit in a four-by-two
          grid with roles rather than just names — VP of sales, head of
          logistics, operations lead, founder — because the buyer here is
          usually shipping for a business, not posting a single parcel. A
          partners band carrying the TCS, Leopards and PayPro marks follows, and
          borrowing recognisable courier and payment brands does more for trust
          than any claim the site could make about itself. A mobile app
          announcement with store badges closes the page on momentum.
        </p>
        <img
          src="/portfolios/design-portfolio/shipfro/05.webp"
          alt="Shipfro Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The system runs on one red, #FF403A, with #FF9693 as its light tint,
          #131C21 for the footer and text, and white doing most of the work. Red
          is spent only on actions and emphasis — get quote, book, the savings
          figure, the active tab — which is what lets the hero run a full
          red-to-orange gradient without the rest of the page feeling loud. The
          courier brands keep their own yellows and reds, so the palette had to
          stay disciplined enough to sit beside them without clashing.
        </p>
        <img
          src="/portfolios/design-portfolio/shipfro/06.webp"
          alt="Shipfro Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Plus Jakarta Sans carries the whole page, from twelve-pixel table
          labels up to the sixty-pixel hero line. Its slightly geometric,
          friendly forms keep a dense comparison table from reading like a
          spreadsheet, and a single family across headings, form controls and
          tabular data meant the component library stayed small — buttons,
          inputs, chips, table rows and cards were all that the site needed.
        </p>
        <img
          src="/portfolios/design-portfolio/shipfro/07.webp"
          alt="Shipfro Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg border border-gray-200"
        />
        <p>
          Read top to bottom, the page does one job and does it above the fold:
          it turns a comparison people were making across several courier sites,
          from memory, into a single form and a single table. Everything below
          exists to justify booking through it rather than going direct.
        </p>
      </>
    ),
  },
  {
    image: '/portfolios/design-portfolio/portfolio-03.webp',
    mobileImageLink: '/portfolios/design-portfolio/portfolio-mob-03.webp',
    title: 'Stemify',
    subtitle: 'AI Stem Separation Studio for Music Producers',
    alt: 'Stemify Portfolio',
    url: '',
    categories: ['All', 'Dark', 'SaaS', 'Music'],
    description:
      'I designed the marketing site and the studio product UI for Stemify, an AI tool that splits songs into clean stems and a cappellas. I built a multitrack editor with per-stem waveforms, solo/mute controls and key, BPM and time-signature readouts, plus a synced mobile player, and shipped a dark design system covering waveform states, transport controls and sliders. I implemented the responsive front end and the social-proof modules. A technically complex audio product now reads as a simple upload, separate, download flow.',
    skills: [
      'UI/UX Design',
      'Web App Design',
      'Design System',
      'Figma',
      'Interaction Design',
      'Audio / Media Player UI',
      'Dark Mode UI',
      'Mobile App Design',
      'Landing Page Design',
      'Responsive Web Design',
      'React',
      'Tailwind CSS',
      'AI Product Design',
    ],
    article: (
      <>
        <img
          src="/portfolios/design-portfolio/stemify/01.webp"
          alt="Stemify Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Stem separation is a hard thing to sell in words. Every competitor
          claims clean stems, the difference between good and bad separation is
          audible rather than visual, and the failure modes — phasing,
          overlapping, muddiness — are exactly what a producer has been burned
          by before. So the page leads with the promise in the negative, naming
          those failures directly, and then spends the rest of its length
          proving the opposite instead of restating it.
        </p>
        <p>
          The hero stacks three views of the product at once: the parent
          eMastered player above, the compact Stemify panel with its faders and
          transport, and behind them the multitrack list where every separated
          part gets its own colour of waveform. Even at thumbnail size the core
          proposition is legible — one song has become five differently coloured
          things you can touch independently.
        </p>
        <img
          src="/portfolios/design-portfolio/stemify/02.webp"
          alt="Stemify Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The best decision on the page is the section titled A Difference You
          Can Hear, because it is not a screenshot — it is a real, playable
          session sitting in the middle of the marketing site. A track is
          loaded, the original waveform runs across the top, and beneath it sit
          vocal, drums, bass, guitar and piano, each with its own waveform and
          its own mute control. Anyone can toggle parts against each other and
          judge the separation with their ears, which is the only way this
          product can actually be evaluated.
        </p>
        <p>
          The paywall is then placed inside the demo rather than in front of it.
          The lower stems fade under an overlay offering to unlock more
          instrumental sounds at sign-up, with buttons to upload your own track
          or start free. A visitor has already heard it work before being asked
          for anything, and the thing they are being asked to unlock is the
          thing they were just using.
        </p>
        <img
          src="/portfolios/design-portfolio/stemify/03.webp"
          alt="Stemify Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          After the demo the page switches to white and slows down. Three
          alternating sections — elegant features and ultimate control, the best
          tool for making a cappellas or instrumentals, and perfect audio
          exports with a click — are each carried by a 3D illustration of an
          instrument rather than another interface shot: a violet grand piano, a
          guitar, a drum kit, floating over soft pink brushstrokes. Export
          formats appear as two small chips, Mp3 and Wav, which is the entire
          answer to a question producers always ask.
        </p>
        <p>
          Those illustrations are doing deliberate work. A tool that outputs
          stems is abstract and slightly technical, and the instruments put
          familiar objects back in front of the reader between two very dense
          dark sections. Back on black, We Create Stems That Actually Sound
          Great names the six parts the engine isolates — vocals, guitar, drums,
          piano, bass and other — as a grid of tiles with the active one
          outlined in brand pink.
        </p>
        <img
          src="/portfolios/design-portfolio/stemify/04.webp"
          alt="Stemify Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The page closes on the full studio, and this is the screen the whole
          design system was built for. A header strip carries song name, artist,
          key, BPM and time signature — F#m, 140.0, 4/4 — over a ruled timeline
          with a playhead. Below it each stem is a row: a labelled fader, solo
          and mute buttons, and a waveform tinted to that part. Putting the
          musical metadata in a single strip above the timeline is what lets a
          producer confirm they are working on the right thing before touching
          anything.
        </p>
        <img
          src="/portfolios/design-portfolio/stemify/05.webp"
          alt="Stemify Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The identity encodes the product. Stemify’s mark is three offset bars
          in #FF003E, #D8046E and #A409AD — layers of the same shape, pulled
          apart, which is literally what the software does to a song. Those
          three become the palette, and the palette becomes the interface: every
          waveform in the editor is tinted from that ramp so a stem can be
          identified by colour before its label is read, all over a #161B26
          surface that keeps the waveforms as the only bright thing on screen.
        </p>
        <img
          src="/portfolios/design-portfolio/stemify/06.webp"
          alt="Stemify Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Montserrat runs from fourteen-pixel control labels to the
          forty-eight-pixel headline — the same family as the sibling eMastered
          and PitchPro projects, since all three share one component library. In
          the editor it earns its place at the small end: stem names, fader
          values and the key and tempo readout all sit at fourteen and sixteen
          pixels against a dark background and still hold.
        </p>
        <img
          src="/portfolios/design-portfolio/stemify/07.webp"
          alt="Stemify Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg border border-gray-200"
        />
        <p>
          End to end the page is arranged as an argument you can check rather
          than one you have to trust: name the problem, let the visitor hear it
          solved in the browser, soften the technical middle with illustration,
          then show the studio they will be working in. A complex piece of audio
          processing ends up reading as upload, separate, download.
        </p>
      </>
    ),
  },
  {
    image: '/portfolios/design-portfolio/portfolio-04.webp',
    mobileImageLink: '/portfolios/design-portfolio/portfolio-mob-04.webp',
    title: 'LinkMaster',
    subtitle: 'Link-in-Bio Platform for Musicians',
    alt: 'LinkMaster Portfolio',
    url: '',
    categories: ['All', 'Dark', 'SaaS', 'Music', 'Social'],
    description:
      "I designed LinkMaster, a link-in-bio platform that turns an artist's followers into listeners. I created the acquisition flow — a claim-your-URL field in the hero — and the artist profile itself: avatar, bio, contact and follow actions, and a playable song list linked out to Spotify, Instagram, TikTok, Facebook and Snapchat. I delivered the gradient-led visual language and component library, then implemented the responsive front end. Artists now publish a professional, mobile-first presence in minutes.",
    skills: [
      'UI/UX Design',
      'Web Design',
      'Design System',
      'Figma',
      'Mobile-First Design',
      'Landing Page Design',
      'Conversion-Focused Design',
      'Social Media Integration UX',
      'Responsive Web Design',
      'React',
      'Tailwind CSS',
      'HTML5 / CSS3 / JavaScript',
      'SaaS Product Design',
    ],
    article: (
      <>
        <img
          src="/portfolios/design-portfolio/linkmaster/01.webp"
          alt="LinkMaster Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          LinkMaster is the one product in this family that isn’t dark. Its
          siblings are studio tools — near-black chrome, neon accents, built for
          people already at a desk. LinkMaster is the page an artist hands to
          strangers, so it opens on a pastel wash of lavender, mint and blush
          and keeps its headline in plain black. The audience changed, so the
          surface did too.
        </p>
        <p>
          The single most important element is the field under the headline.
          Rather than an email capture, it shows the URL being claimed —
          linkmaster.com slash your name — with a Claim Profile button beside
          it. The first thing a visitor does is not sign up but reserve
          something, and a name you have already typed is much harder to walk
          away from. The same field reappears verbatim at the end of the page,
          so the beginning and the end of the scroll ask for exactly the same
          small commitment.
        </p>
        <img
          src="/portfolios/design-portfolio/linkmaster/02.webp"
          alt="LinkMaster Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Everything the product does is shown on a phone. The feature rows walk
          through creating the page, adding as many tracks as you like, sharing
          media, pulling streams from twenty-five-plus apps and listing social
          profile links — and every one of them is a mobile screenshot, because
          a link-in-bio page is almost never seen on a desktop. Small floating
          chips sit over each mockup to point at the control being described.
        </p>
        <p>
          The artist profile itself is the thing being sold, and it appears
          before any feature is explained: cover image, avatar, name, the line
          about being a singer-songwriter signed to a label, contact and follow
          buttons, then a list of songs each with its own play control. A
          musician can see their own page in it immediately, which is the whole
          job of that first screen.
        </p>
        <img
          src="/portfolios/design-portfolio/linkmaster/03.webp"
          alt="LinkMaster Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Then the page goes dark exactly once, for the section about sharing
          the link everywhere. A cluster of oversized 3D app icons — Spotify,
          Facebook, TikTok, LinkedIn, Instagram, Snapchat — spills across the
          left half, and the copy adds the offline case: a QR code that brings
          an audience that met you at a gig back to the same page.
        </p>
        <p>
          Rendering the platforms as physical objects rather than flat glyphs is
          the right call for this product. LinkMaster’s value is entirely
          borrowed from services it does not own, so the page has to make those
          services feel present and tangible — the icons are the argument, and
          the single dark section makes them glow.
        </p>
        <img
          src="/portfolios/design-portfolio/linkmaster/04.webp"
          alt="LinkMaster Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The close repeats the claim field and then shows three finished
          profiles side by side — different artists, different cover art, the
          same layout. Social proof and product demo are the same image here:
          proof that other musicians use it, and proof that a page looks good
          whoever fills it in. Underneath, a pastel footer bar states the
          lineage plainly — LinkMaster is brought to you by eMastered — which
          borrows the parent brand’s credibility for what is otherwise a very
          young product.
        </p>
        <img
          src="/portfolios/design-portfolio/linkmaster/05.webp"
          alt="LinkMaster Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The hero gradient is a three-stop ramp — #E5BAFF lavender into #88FCF1
          mint into #FFBEBE blush — and it does more than decorate. It is the
          one thing separating this product from its siblings at a glance, it
          returns as the footer bar to close the page as it opened, and it gives
          a screenshot-heavy layout something to sit on that isn’t another grey
          card. The family’s #D8046E stays for buttons and links, so LinkMaster
          still reads as part of the same house.
        </p>
        <img
          src="/portfolios/design-portfolio/linkmaster/06.webp"
          alt="LinkMaster Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Montserrat carries the type again, but at Black rather than the
          SemiBold used across the studio products — sixty pixels of it in the
          hero, with the word Audience filled by a violet-to-red gradient. The
          heavier weight is doing tonal work: this is a consumer page competing
          for attention on a phone, not a tool being operated, and it can afford
          to be louder than the software it belongs to.
        </p>
        <img
          src="/portfolios/design-portfolio/linkmaster/07.webp"
          alt="LinkMaster Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg border border-gray-200"
        />
        <p>
          Read end to end, the page is one long answer to a single question:
          what will my page look like. It shows a finished profile before it
          explains anything, demonstrates each feature on the device it will
          actually be seen on, and asks only for a username — so publishing a
          professional presence really does come down to typing a name and
          pressing a button.
        </p>
      </>
    ),
  },
  {
    image: '/portfolios/design-portfolio/portfolio-05.webp',
    mobileImageLink: '/portfolios/design-portfolio/portfolio-mob-05.webp',
    title: 'PitchPro',
    subtitle: 'Vocal Tuning Plugin — Product UI & Launch Site',
    alt: 'PitchPro Portfolio',
    url: '',
    categories: ['All', 'Dark', 'SaaS', 'Music'],
    description:
      "I designed PitchPro's plugin interface and its launch site. I built the vocal-tuning UI around a central pitch dial with Flex-Tune, Retune Speed, Humanize and Vibrato knobs plus key, scale, detune and transpose controls, and standardised the knobs, meters and panels into a reusable kit. I designed the dark, neon-lit landing page and implemented the responsive front end around a free-trial funnel. The plugin's core value is legible before anyone downloads it.",
    skills: [
      'UI/UX Design',
      'Product UI Design',
      'Design System',
      'Figma',
      'Skeuomorphic / Plugin Interface Design',
      'Interaction Design',
      'Dark Mode UI',
      'Landing Page Design',
      'Responsive Web Design',
      'React',
      'Tailwind CSS',
      'Audio Software UX',
    ],
    article: (
      <>
        <img
          src="/portfolios/design-portfolio/pitchpro/01.webp"
          alt="PitchPro Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Audio plugins are bought by people who already own several that do
          almost the same thing, which makes the landing page’s job unusually
          narrow: show the interface, and make it obvious in one look that this
          one is easier to drive. So PitchPro opens with three words — your
          voice, perfected — and then hands the rest of the screen to the plugin
          itself.
        </p>
        <p>
          The plugin is the argument. A pitch dial sits dead centre with its
          correction arc lit in the brand gradient, four labelled knobs around
          it for flex-tune, retune speed, humanize and vibrato, key, scale,
          detune and transpose along the bottom edge, and a piano keyboard
          running the full width beneath. Everything a vocal producer touches is
          on one face, at one depth, with no tabs to open — and putting that on
          the hero rather than a feature further down is what lets a visitor
          judge the tool before reading a word about it.
        </p>
        <img
          src="/portfolios/design-portfolio/pitchpro/02.webp"
          alt="PitchPro Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Below the fold the page flips to white and walks the capabilities one
          at a time — pinpoint pitch correction, stunning harmonies, an
          all-in-one FX suite — each as an alternating row pairing a paragraph
          with a screenshot of the actual panel that does the work. Small
          labelled chips float over each screenshot calling out specific
          controls, so the reader is being shown where a feature lives rather
          than just told it exists.
        </p>
        <p>
          Switching from the near-black hero to white here is deliberate. The
          dark chrome sells the instrument; the light section sells the
          documentation, and the contrast keeps a long page from reading as one
          undifferentiated scroll.
        </p>
        <img
          src="/portfolios/design-portfolio/pitchpro/03.webp"
          alt="PitchPro Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The most economical thing on the page is the row of three knobs under
          Get Control of Every Note. The same control is drawn at nought, fifty
          and one hundred: unlit at rest, ringed in cyan halfway, ringed in
          magenta at full. One image explains the entire feedback model of the
          interface — how far a value has travelled is a colour temperature, not
          a number to read — and it does it without a caption.
        </p>
        <p>
          Underneath, Sculpt Your Sound with Precision lists the FX rack as a
          reorderable stack — chorus, flanger, tremolo, compressor, distortion,
          delay — each tagged with its own colour dot and a drag handle. It is
          the signal chain shown as a list, which is how a producer thinks about
          it, and it quietly promises that the order is theirs to change.
        </p>
        <img
          src="/portfolios/design-portfolio/pitchpro/04.webp"
          alt="PitchPro Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The page then answers the question a crowded category always raises —
          why did we create PitchPro — with three plain claims: easy to use,
          all-in-one, made for every style. A gradient card follows inviting the
          visitor to hear the difference themselves, and the funnel keeps
          returning to the same offer: try it for free, with a fourteen-day
          money-back guarantee printed under the button rather than buried in
          terms. Press logos, an artist mosaic under a Grammy-winning credit
          line, a five-star testimonial and an FAQ fill the space between, and
          the page closes by repeating the opening headline over the plugin one
          last time.
        </p>
        <img
          src="/portfolios/design-portfolio/pitchpro/05.webp"
          alt="PitchPro Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The identity is a gradient rather than a colour: #F905D3 magenta
          running to #06AFFE cyan, over a #121317 surface with white type. That
          single ramp does every job on the product — it fills the wordmark,
          lights the pitch arc, marks the active knob ring, and coats the
          call-to-action buttons — which is what ties a plugin face and a
          marketing page together without either borrowing the other’s layout.
          Against near-black it also reads as emitted light rather than paint,
          which is the right register for a piece of studio equipment.
        </p>
        <img
          src="/portfolios/design-portfolio/pitchpro/06.webp"
          alt="PitchPro Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Montserrat carries the type from fourteen-pixel control labels up to
          the seventy-two-pixel display line. Its geometric caps suit a hardware
          face — knob labels set in small caps read like screen printing on a
          panel — while the wide display sizes give the marketing headlines
          enough presence to hold their own above a screenshot that is competing
          hard for attention.
        </p>
        <img
          src="/portfolios/design-portfolio/pitchpro/07.webp"
          alt="PitchPro Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg border border-gray-200"
        />
        <p>
          End to end, the page is a demo dressed as a landing page. The plugin
          appears in the first screen, again in every feature row, again as
          three knob states, and again at the close — so by the time someone
          reaches the free trial they have effectively already used it, and the
          download is a confirmation rather than a gamble.
        </p>
      </>
    ),
  },
  {
    image: '/portfolios/design-portfolio/portfolio-06.webp',
    mobileImageLink: '/portfolios/design-portfolio/portfolio-mob-06.webp',
    title: 'eMastered',
    subtitle: 'AI Mastering Studio — Marketing Site & App Dashboard',
    alt: 'eMastered Portfolio',
    url: '',
    categories: ['All', 'Dark', 'SaaS', 'Music'],
    description:
      'I designed both halves of eMastered, an AI mastering engine: a conversion-driven landing page and the studio users land in after signup. I built the app shell with sidebar navigation for Discover, Favorites, Library, Profile and Account, a prominent New Master action, recent-track lists and a video-led onboarding module. I extended the shared design system across both surfaces and implemented the responsive front end. One visual language now runs from advert to first master, with the primary action always a click away.',
    skills: [
      'UI/UX Design',
      'Web App Design',
      'Dashboard Design',
      'Design System',
      'Figma',
      'Onboarding UX',
      'Dark Mode UI',
      'Landing Page Design',
      'Responsive Web Design',
      'React',
      'Tailwind CSS',
      'AI Product Design',
      'SaaS Product Design',
    ],
    article: (
      <>
        <img
          src="/portfolios/design-portfolio/emastered/01.webp"
          alt="eMastered Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          This page sells the half of eMastered that turns a mastering tool into
          a livelihood: not just clean audio, but a storefront for the beats a
          producer makes. The headline says it without hedging — turn your beats
          into a business — and the proof underneath it is the product, shown as
          a full application window rather than a cropped feature.
        </p>
        <p>
          That screenshot is doing a lot of work. Its top bar names the four
          things the platform does — mastering, distribution, library,
          marketplace — so the marketplace reads as one room in a larger house
          rather than a separate product. Its sidebar opens with a single
          gradient Create button above everything else, then groups the rest
          under plain headings: Marketplace for shopping beats, sound kits and
          artists, then My Content, Marketing and Performance. A producer can
          see their whole working week in one screenshot, and see that making
          something is always one click from wherever they are.
        </p>
        <img
          src="/portfolios/design-portfolio/emastered/02.webp"
          alt="eMastered Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Three promises follow — a customisable profile page, global reach and
          seamless transactions — and then the page immediately shows the
          profile it just promised. The artist page carries a cover, a producer
          name and tier, play counts and follower numbers, a follow button, a
          row of earned achievement badges and a grid of tracks. Showing the
          artefact straight after the claim is the whole rhetorical move of this
          page: say it once, then show the screen where it happens.
        </p>
        <p>
          Discovery gets the same treatment. The beat browser leads with search
          and then a full row of filters — genre, track type, mood, BPM, energy,
          vocal — because a buyer looking for a beat is not browsing, they are
          matching something they can already hear in their head.
        </p>
        <img
          src="/portfolios/design-portfolio/emastered/03.webp"
          alt="eMastered Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Between the product sections sit two tilted mosaics, and they are the
          most purely emotional thing on the page. One is a wall of cover art
          for selling tracks and building a legacy; the other is a wall of faces
          for connecting with artists. Angling both grids and letting them bleed
          off the edge implies a catalogue far larger than the tiles actually
          shown — a marketplace has to feel populated before anyone will list on
          it, and this is how the page manufactures that feeling without
          inventing a number.
        </p>
        <img
          src="/portfolios/design-portfolio/emastered/04.webp"
          alt="eMastered Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Pricing is where the mastering engine reasserts itself. Three plans
          run yearly, yearly-paid-upfront and monthly, with the middle one
          badged most popular and lit in the brand gradient, and each column
          lists what it includes with ticks — and, importantly, what it does not
          with greyed-out crosses. A shared block beneath them covers what every
          plan gets regardless: advanced mastering options, reference mastering,
          cloud storage, a shareable library page and track statistics.
        </p>
        <p>
          Using crosses rather than simply omitting the missing rows is the
          honest choice and the more persuasive one — the cheaper column visibly
          stops, so the jump to the recommended plan explains itself without a
          sales line. The rest of the page closes the way the category expects:
          reasons the marketplace exists, a gradient invitation to try it, an
          FAQ that answers what mastering even is, a five-star testimonial, and
          a final repeat of the app above one more free-trial button.
        </p>
        <img
          src="/portfolios/design-portfolio/emastered/05.webp"
          alt="eMastered Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The palette is a single brand magenta, #D8046E, with #A409AD as the
          violet end of the gradient it resolves into, over a #1F242F surface
          and #F5F5F6 text. That gradient is reserved almost entirely for
          commitment — the logo mark, the Create button, the most-popular plan,
          the closing call to action — while everything else is greyscale on
          near-black. In a product whose screens are already full of album art
          in every colour imaginable, keeping the interface itself monochrome is
          what stops the catalogue and the chrome from fighting.
        </p>
        <img
          src="/portfolios/design-portfolio/emastered/06.webp"
          alt="eMastered Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Montserrat runs from fourteen-pixel table labels to the sixty-pixel
          headline, the same family and the same scale used across the sibling
          plugin project — which is what let both surfaces share a component
          library while still reading as different products. Its wide, even caps
          hold up over busy artwork, and at small sizes it keeps filter chips
          and plan features legible against a dark background.
        </p>
        <img
          src="/portfolios/design-portfolio/emastered/07.webp"
          alt="eMastered Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg border border-gray-200"
        />
        <p>
          Read end to end, the page argues that mastering was only ever the
          first step. It opens on the application, keeps returning to it after
          every claim, prices the engine that powers it, and closes on the same
          screen it opened with — so the free trial reads less like a download
          and more like being handed the keys to a shop.
        </p>
      </>
    ),
  },
  {
    image: '/portfolios/design-portfolio/portfolio-07.webp',
    mobileImageLink: '/portfolios/design-portfolio/portfolio-mob-07.webp',
    title: 'Emega Technologies',
    subtitle: 'Freelancing & Outsourcing Company Website',
    alt: 'Emega Portfolio',
    url: '',
    categories: ['All', 'Light', 'Agency', 'SaaS'],
    description:
      "I designed and built Emega Technologies' website to turn visitors into project enquiries. I created a light, editorial homepage around a photo-mosaic hero, plus About, Pricing, Freelancing, Projects, Domains and Mobile App sections and an illustrated explanation of project timelines and the 24-hour task cycle. I delivered the blue-accented component library and implemented the responsive front end. A services business now tells a clear, human story instead of leaning on a stock corporate template.",
    skills: [
      'UI/UX Design',
      'Web Design',
      'Design System',
      'Figma',
      'Information Architecture',
      'Illustration & Visual Storytelling',
      'Landing Page Design',
      'Responsive Web Design',
      'React',
      'Tailwind CSS',
      'HTML5 / CSS3 / JavaScript',
      'Agency / Corporate Website Design',
    ],
    article: (
      <>
        <img
          src="/portfolios/design-portfolio/emega/01.webp"
          alt="Emega Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Every software house says the same four things, so the hard part of an
          agency site is sounding like a specific company rather than a
          category. Emega opens with a promise instead of a description — you
          dream, we build — set in a gradient that runs from the brand blue into
          magenta, with a fifteen-day challenge sitting directly underneath it.
          A number is harder to fake than an adjective, and it gives a visitor
          something to hold onto before any of the credentials arrive.
        </p>
        <p>
          The navigation makes an unusual choice and puts the logo in the middle
          of the bar rather than the left, splitting the links either side of
          it. It reads as a brand mark first and a menu second, which suits a
          company whose main job on this page is to be remembered.
        </p>
        <p>
          What follows is the credibility work, and it is deliberately written
          as answers rather than boasts. Reasons to choose us leads into a
          plainly worded question — why would you ever want to work with us —
          and four staggered cards reply: project management, quality assured,
          cheaper prices, in-house domain experts. The stagger keeps the eye
          moving through them instead of scanning a rigid grid and stopping.
        </p>
        <img
          src="/portfolios/design-portfolio/emega/02.webp"
          alt="Emega Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The most interesting decision on the site is that the services are
          priced. Most agencies hide behind a contact form; Emega publishes
          development plans as subscriptions — a starter tier, a highlighted
          team tier and a custom company tier — each listing exactly what is
          included, from feedback categorisation and prioritisation to real-time
          collaboration and dev-tool integrations. Only the custom tier routes
          to a conversation. Publishing the first two numbers is what turns a
          services business into something a visitor can evaluate on their own.
        </p>
        <p>
          Above the plans sits a row of domain chips — education, health,
          climate, legal, agriculture, water — that doubles as positioning. A
          generalist agency claiming to serve every industry is not believable,
          so naming six sectors and letting the visitor pick theirs makes the
          same claim credible. The rest of the page supports it: a success story
          on solar-farming research with numbered project cards, an app section
          that lays the accepted payment methods out along an arc, and client
          marks from recognisable publishers.
        </p>
        <img
          src="/portfolios/design-portfolio/emega/03.webp"
          alt="Emega Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Behind the site is the product that actually runs the business, and it
          is built for four separate audiences — client, employee, freelancer
          and admin — each with its own navigation inside one shell. The client
          view opens on project analytics: an active project broken into UI/UX,
          front-end and back-end completion against a single ring, spending
          totals across a twelve-month chart, wages, subscriptions and assets,
          and a recent-chat list where every conversation is labelled with its
          project and domain rather than just a name.
        </p>
        <p>
          A right-hand rail carries the things a client checks rather than works
          in — profile, project count, spend, score, and a progress card with
          its own gauge — which keeps the centre column free for the work
          itself. The same rail is where the upsell lives, so the commercial
          prompt never interrupts the reporting.
        </p>
        <img
          src="/portfolios/design-portfolio/emega/04.webp"
          alt="Emega Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The admin view is a different product wearing the same clothes. Where
          the client sees analytics, an administrator sees a queue: incoming
          partner requests on the left, the selected person’s bio, status,
          requested skills and attached résumé in the middle, and accept and
          reject as the only two things to do on the right. Colour does the
          sorting — blue to approve, soft red to decline — and a message
          composer sits under the profile so a question can be asked before a
          decision is made. Alongside them sit messages, projects, payments,
          requests and a chat-bot page, and the marketing site adds About,
          Pricing, Freelancing, Projects, Domains and Mobile App pages plus
          sign-in and sign-up.
        </p>
        <img
          src="/portfolios/design-portfolio/emega/05.webp"
          alt="Emega Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The palette is a muted slate blue, #7086B5, with #505050 for text and
          white carrying the space, and it is the restraint that makes the one
          flourish work: a gradient running from that blue to #D735FF, spent
          only on the hero line, the prices and the primary buttons. Everything
          else stays quiet, so the gradient reads as a brand signature rather
          than decoration — and the same device carries into the dashboard,
          where it marks the single call to action on an otherwise sober screen.
        </p>
        <img
          src="/portfolios/design-portfolio/emega/06.webp"
          alt="Emega Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Roboto runs the whole system, from eighteen-pixel input text through
          twenty-four-pixel subheads and thirty-pixel section headings up to the
          sixty-five-pixel hero in ExtraBold. Choosing a neutral workhorse was
          the right call for a project that had to cover marketing pages,
          illustration-heavy storytelling and four data-dense dashboards: the
          personality comes from the colour and the illustration, and the type
          stays out of the way and stays readable at chart-label sizes.
        </p>
        <img
          src="/portfolios/design-portfolio/emega/07.webp"
          alt="Emega Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg border border-gray-200"
        />
        <p>
          End to end, the work turns a services company into something closer to
          a product. The site names the sectors, publishes the prices and makes
          a concrete promise about time; the platform then keeps that promise
          visible, giving clients, employees, freelancers and administrators
          each their own view of the same projects.
        </p>
      </>
    ),
  },
  {
    image: '/portfolios/design-portfolio/portfolio-08.webp',
    mobileImageLink: '/portfolios/design-portfolio/portfolio-mob-08.webp',
    title: 'Mystic Dome',
    subtitle: 'Event Discovery & Ticketing Platform',
    alt: 'Mystic Dome Portfolio',
    url: '',
    categories: [
      'All',
      'Light',
      'AI',
      'Social',
      'SaaS',
      'Real Estate',
      'Music',
    ],
    description:
      'I designed Mystic Dome, an event discovery and ticketing platform for the Gulf market. I built a full-bleed featured-event hero carrying price, date and venue, nearby-event search, city selection across Dubai and Abu Dhabi, language and currency switching (ENG/AED), and the cart and checkout flows. I delivered the purple-accented design system and implemented the responsive front end for the beta. Discovery, city choice and purchase now live in one uninterrupted scroll.',
    skills: [
      'UI/UX Design',
      'Web Design',
      'Design System',
      'Figma',
      'E-commerce & Checkout UX',
      'Search & Filtering UX',
      'Localisation (Multi-currency / Multi-language)',
      'Landing Page Design',
      'Responsive Web Design',
      'React',
      'Tailwind CSS',
      'Marketplace Platform Design',
    ],
    article: (
      <>
        <img
          src="/portfolios/design-portfolio/mystic-dome/01.webp"
          alt="Mystic Dome Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Mystic Dome looks like a ticketing site and is really a two-sided
          marketplace. On one side, party seekers find events and buy tickets.
          On the other, event owners and planners assemble an event by hiring
          the performers, vendors, venues and influencers who make it happen.
          The design had to make the first side feel effortless without hiding
          the machinery that serves the second.
        </p>
        <p>
          The consumer entrance is a full-bleed featured event carrying
          everything a decision needs — category, date, venue and the ticket
          price on the button itself — over a nearby-events search, a language
          switch and a currency switch that default to English and dirhams for
          the Gulf. Below it, Select Your City turns geography into the first
          filter, and each city card shows an avatar stack and an ongoing-event
          count so a quiet city looks quiet. Dubai and Abu Dhabi lead, with
          Prague, Amsterdam, Berlin and Istanbul behind them.
        </p>
        <img
          src="/portfolios/design-portfolio/mystic-dome/02.webp"
          alt="Mystic Dome Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The rest of the homepage is one uninterrupted scroll of event cards —
          top events this weekend, a full-width featured banner, then top
          attractions and experiences. Every card repeats the same four facts
          and ends in two buttons: a clock to save it for later and a purple bag
          to add it to the cart. Two intents, one card, no detour through a
          detail page for someone who already knows what they want.
        </p>
        <p>
          Signing in never leaves the page either. Log in, sign up and password
          reset are modals over the homepage, so an account is something you
          acquire mid-browse rather than a wall you hit first — which matters
          when city selection and checkout both want you signed in.
        </p>
        <img
          src="/portfolios/design-portfolio/mystic-dome/03.webp"
          alt="Mystic Dome Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The event page is where the two sides meet, and it is a structured
          brief rather than a poster. Performers are declared as a taxonomy —
          performer type, then musician type down to acapella or accordion, then
          dancer type down to adowa or afro-beat. Tickets are split into
          standard and VIP, each with price, availability and an estimated
          total. Other details capture the things that decide whether a supplier
          can serve the job at all: dress code, spelled out with examples, and
          whether it is a kids event.
        </p>
        <p>
          Writing an event down this precisely is the decision the whole
          platform rests on. A free-text listing can be read by a human but not
          matched by a system; a taxonomy can be filtered, so performers and
          vendors see only the events they can actually serve.
        </p>
        <img
          src="/portfolios/design-portfolio/mystic-dome/04.webp"
          alt="Mystic Dome Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          That structure pays off on the supply side. When a performer bids, the
          event owner opens their profile as a panel sliding over the event
          rather than a separate page, so the brief stays in view behind the
          candidate. The profile leads with a verified badge and an active
          status, then the bid amount in dirhams, a rating with its review
          count, and the performer’s own type breadcrumb echoing the same
          taxonomy the brief used — accordion, acapella, afro-beat. Two actions
          close it: dismiss, or hire.
        </p>
        <p>
          Roles are handled in the component library rather than by duplicating
          screens. One navbar is a variant set spanning party seeker, event
          owner, pro planner and bidder, with properties for signed-in state,
          whether the menu or profile is open, and which tab is selected — four
          products wearing one chrome. Around it sit the pieces the pages are
          assembled from: event cards, city cards, an event carousel, headers,
          galleries, list input fields, a login carousel, the event page and the
          bidder profile. The file also carries dedicated sign-up and log-in
          pages, create-event flows for both event owners and pro planners,
          view-event flows for party seekers and for performers, vendors, venue
          providers and influencers, and a set of email templates.
        </p>
        <img
          src="/portfolios/design-portfolio/mystic-dome/05.webp"
          alt="Mystic Dome Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The palette is a single purple, #9162CA, with #684297 for its deeper
          states, #101828 for text and #F2F4F7 for surfaces. Almost every screen
          is white, and the purple is rationed to actions and prices — the
          add-to -cart button, the ticket price, the bid amount, the selected
          tab. Event photography is doing the emotional work, so the interface
          stays out of its way; a brighter palette competing with concert
          lighting would make both look cheap.
        </p>
        <img
          src="/portfolios/design-portfolio/mystic-dome/06.webp"
          alt="Mystic Dome Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Inter runs from fourteen-pixel card metadata to the sixty-pixel hero
          line. The demand on it here is range rather than character: the same
          family has to carry a cinematic hero over a photograph and a
          three-level taxonomy of performer types in a form, and stay neutral
          enough that AED prices and review counts read as facts.
        </p>
        <img
          src="/portfolios/design-portfolio/mystic-dome/07.webp"
          alt="Mystic Dome Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg border border-gray-200"
        />
        <p>
          Taken together, discovery, city choice and purchase live in one
          uninterrupted scroll for the party seeker, while the same event record
          becomes a biddable brief for everyone who supplies it. The beta
          shipped with both sides sharing one component library.
        </p>
      </>
    ),
  },
  {
    image: '/portfolios/design-portfolio/portfolio-09.webp',
    mobileImageLink: '/portfolios/design-portfolio/portfolio-mob-09.webp',
    title: 'The Sustainable City - Yiti',
    subtitle: 'Bilingual Real Estate Experience, Oman',
    alt: 'TSC Yiti Portfolio',
    url: '',
    categories: ['All', 'Light', 'Real Estate'],
    description:
      'I ran the UX research and shaped the digital brand for The Sustainable City – Yiti, a master-planned community in Oman sold to a bilingual audience. I designed a cinematic aerial hero with 360° tour entry, an Arabic/English switch, a Find Your Dream Home journey and sustainability storytelling across social, environmental and economic pillars. I delivered the visual identity application, page templates and the register-interest flow. The site sells a lifestyle first and a property second, feeding qualified enquiries to sales.',
    skills: [
      'UX Research',
      'Brand Strategy',
      'Visual Identity',
      'Art Direction',
      'User Personas & Empathy Mapping',
      'Web Design',
      'Figma',
      'High-Fidelity Prototyping',
      'Bilingual / RTL Design (Arabic & English)',
      'Real Estate Marketing UX',
      'Lead Generation Flows',
      'Design Guidelines & Documentation',
    ],
    article: (
      <>
        <img
          src="/portfolios/design-portfolio/tsc-yiti/01.webp"
          alt="TSC Yiti Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The Sustainable City – Yiti is a master-planned community on the Omani
          coast, and a website for one has an unusual problem: nothing exists
          yet to photograph, and the thing being sold is a way of living rather
          than a building. So the site opens on an aerial render of the whole
          community with four words over it — a sustainable lifestyle for a
          better tomorrow — and no price, no floor plan and no form. A language
          toggle to Arabic sits immediately beside the logo, because for this
          audience bilingualism is not a setting buried in a footer.
        </p>
        <p>
          Sustainability is then structured rather than asserted. A row of three
          hexagons switches between the social, environmental and economic
          pillars, each with its own colour — orange for social, a green for
          environmental, teal for economic — and its own list of specifics. The
          economic panel does not talk about values; it lists job creation,
          savings on electricity and water bills, support for local businesses
          and in-country value, lower operational costs and a circular economy.
          Anyone who has read a developer’s sustainability page knows how rare
          that specificity is.
        </p>
        <img
          src="/portfolios/design-portfolio/tsc-yiti/02.webp"
          alt="TSC Yiti Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The master plan is the emotional centre of the page, and it is
          interactive rather than illustrative. Pins across the aerial name what
          is actually being built — an autism centre, an equestrian centre, a
          research lab, a sustainable mosque, greenhouses, a school, a sports
          complex, a wellness centre, a five-star resort, a plaza and a
          beachfront — and a row of filters underneath re-reads the same map by
          activity: trekking, jogging, equestrian, cycling, wellness, e-buggy.
          The same picture answers two different questions depending on who is
          asking.
        </p>
        <p>
          Below it, the project location is drawn as a stylised map with drive
          times to the places a buyer will actually measure the site against —
          Muscat Airport, the Sultan Qaboos Grand Mosque, the Royal Opera House,
          the National Museum — each about half an hour away. Distance is the
          first objection to a new development outside a city, so it is answered
          before it is raised.
        </p>
        <img
          src="/portfolios/design-portfolio/tsc-yiti/03.webp"
          alt="TSC Yiti Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Only after the lifestyle has been sold does the site sell property.
          Choose Your Villa steps between a four-bedroom courtyard villa, a
          three-bedroom courtyard villa and a four-bedroom garden villa, each
          with a full-width render, a paragraph of description, and a features
          bar reading off bedrooms, private garden, area, bathrooms and parking,
          with Register Interest at the end of it. A zero-service-fees badge
          sits in the corner — the running cost being one of the strongest
          arguments a sustainable community has.
        </p>
        <img
          src="/portfolios/design-portfolio/tsc-yiti/04.webp"
          alt="TSC Yiti Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Choosing a villa opens its own page, and that is where the pitch turns
          into a specification. The floor plan sits between two rails: on the
          left an accordion switching between ground and first floor with a
          download button under it, on the right the accommodation counted out —
          three bedrooms, a maid’s room, five bathrooms, living room, kitchen,
          laundry, parking and a private garden. The plan itself is drawn
          furnished, with the garden landscaped, so it can be read by someone
          who has never read a floor plan before.
        </p>
        <p>
          This is the screen one of the annotations in the file is written
          about: the left menu was specified to stay fixed while the page
          scrolls the length of the plan, so the floor switch is never lost
          behind the drawing. It is a small rule, and it is the difference
          between a two-storey villa being comparable and being confusing.
        </p>
        <img
          src="/portfolios/design-portfolio/tsc-yiti/05.webp"
          alt="TSC Yiti Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Prime Location in Master Plan then answers the question a floor plan
          cannot: where in the community this particular house sits. The plot is
          outlined in orange over the same aerial the homepage used, an info
          card names its district and links back into the full master plan, and
          a Nearby Amenities carousel places the plaza, autism village, mosque
          and equestrian centre around it, each with a photograph and a line of
          description. Reusing the homepage aerial rather than drawing a new map
          is what makes the two scales feel like one place.
        </p>
        <p>
          The page ends on Other Villa Types, so a visitor who has just ruled
          this villa out is handed the next one instead of the back button — and
          the same Register Interest and Download Brochure pair that opened the
          page is repeated after every section.
        </p>
        <p>
          The journey then continues into Find Your Dream Home, which splits the
          community into Sustainable Plaza and Sustainable District before
          explaining what living there means in eight concrete facilities:
          shared EV network, wastewater recycling, thermal insulation, renewable
          energy, EV charging, smart homes, low-carbon embodiment and food
          production.
        </p>
        <img
          src="/portfolios/design-portfolio/tsc-yiti/06.webp"
          alt="TSC Yiti Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The file behind all of this is a production document rather than a
          gallery. Every page — homepage, villa, find your villa, find your
          dream home, sustainable living, about us, newsroom, news, blog,
          register interest with its thank-you state, and a separate broker
          registration — is drawn three times, at MacBook Pro 16, iPad Pro 11
          and iPhone, with dedicated menubars for the smaller two.
        </p>
        <p>
          It also carries the reasoning. Sticky notes beside the frames explain
          why filtered villas float to the top with the greyed-out ones beneath
          them, why the floor-plan side menu stays fixed while the page scrolls
          so interactivity is never lost, why card descriptions live on the
          listing rather than the villa page, and why one section’s cards differ
          in size from their counterparts on the homepage. Writing the rationale
          next to the design is what turns a handoff into documentation, and it
          is the part that survives after the designer leaves the project.
        </p>
        <img
          src="/portfolios/design-portfolio/tsc-yiti/07.webp"
          alt="TSC Yiti Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The palette is the identity’s own logic made into tokens. The brand
          mark is a hexagon built from three interlocking strokes in green, teal
          and orange — the three pillars — so the interface simply reuses them:
          #E48C36 for social, #058DA6 for economic, and a green for
          environmental, over #777777 body text and #E4E4E4 dividers on white.
          Sections are white or teal and nothing else, which lets the CGI carry
          all the colour and keeps a page full of renders from turning into a
          collage. The hexagon repeats as a system too — social icons, photo
          clusters, map pins and pillar tabs are all cut from the same shape.
        </p>
        <img
          src="/portfolios/design-portfolio/tsc-yiti/08.webp"
          alt="TSC Yiti Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Poppins runs the type from thirteen-pixel captions to the
          forty-nine-pixel headline, in a tight scale of seven steps. Its even,
          geometric forms hold up beside Arabic set at the same weight, which
          mattered here more than personality: the bilingual lockup in the
          footer places the Arabic wordmark directly above the English one, and
          the two have to look like one brand rather than a translation.
        </p>
        <img
          src="/portfolios/design-portfolio/tsc-yiti/09.webp"
          alt="TSC Yiti Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg border border-gray-200"
        />
        <p>
          Read end to end, the homepage sells a lifestyle first and a property
          second: the render, then the pillars, then the master plan, then the
          location, and only then the villas and the register-interest form. By
          the time a visitor reaches the form they have already decided what
          kind of place this is, which is exactly what makes the enquiries
          behind it worth a sales team’s time.
        </p>
        <img
          src="/portfolios/design-portfolio/tsc-yiti/10.webp"
          alt="TSC Yiti Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg border border-gray-200"
        />
        <p>
          The villa page repeats that order at the scale of a single home —
          render, then specification, then position within the community, then
          the neighbouring villa types — and carries the same enquiry prompt
          down the whole length of it. Two pages, one argument, told twice at
          different altitudes.
        </p>
      </>
    ),
  },
  {
    image: '/portfolios/design-portfolio/portfolio-10.webp',
    mobileImageLink: '/portfolios/design-portfolio/portfolio-mob-10.webp',
    title: 'Hounter',
    alt: 'Hounter Portfolio',
    url: '',
    categories: ['All', 'Light', 'Real Estate'],
    article: (
      <>
        <img
          src="/portfolios/design-portfolio/hounter/01.webp"
          alt="Hounter Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Embark on a seamless journey to discover your ideal living space with
          Hounter, the ultimate destination for finding your dream home. Hounter
          redefines the home search experience with its user-friendly interface
          and comprehensive property listings, making the process more efficient
          and enjoyable.
        </p>
        <p>
          Hounter, a leading real estate platform, offers users an intuitive
          online experience to explore, compare, and select homes that meet
          their specific needs. From luxurious villas to cozy apartments,
          Hounter’s extensive database ensures all types of buyers can find
          their match.
        </p>
        <img
          src="/portfolios/design-portfolio/hounter/02.webp"
          alt="Hounter Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The Hounter website was designed with the user in mind, featuring a
          clean, modern layout that prioritizes ease of navigation. Key elements
          include a powerful search function, clear categorization of property
          types, and interactive maps for an enriched browsing experience.
        </p>
        <p>
          The site utilizes a calming palette of blues and light greens,
          instilling a sense of trust and tranquility, which are crucial in the
          real estate industry. The typography is crisp and readable, enhancing
          user engagement and ensuring information is easily digestible.
        </p>
        <p>
          Built on robust technologies like HTML5, CSS3, and JavaScript, and
          leveraging frameworks such as React for responsive design, Hounter
          provides a seamless experience across all devices. Tools like Figma
          were used in the design phase for prototyping, ensuring a consistent
          visual and functional execution.
        </p>
        <p>
          Key Features:
          <ul>
            <li>Advanced Search Filters.</li>
            <li>Real-time Property Updates.</li>
            <li>Customer Reviews.</li>
          </ul>
        </p>
        <img
          src="/portfolios/design-portfolio/hounter/03.webp"
          alt="Hounter Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg border border-gray-200"
        />
        <p>
          Hounter has transformed the way buyers and renters search for homes by
          combining sophisticated technology with user-centric design. The
          platform not only simplifies the discovery process but also empowers
          users with the tools and information needed to make informed
          decisions. As a result, Hounter stands out as a beacon of innovation
          in the real estate industry, driving forward a new era of home
          searching.
        </p>
      </>
    ),
  },
  {
    image: '/portfolios/design-portfolio/portfolio-11.webp',
    mobileImageLink: '/portfolios/design-portfolio/portfolio-mob-11.webp',
    title: 'The Sustainable City',
    subtitle: 'Flagship Community Website, Diamond Developers',
    alt: 'TSC Portfolio',
    url: '',
    categories: ['All', 'Light', 'Real Estate'],
    description:
      "I ran user research and designed the UI and design system for The Sustainable City, Diamond Developers' flagship community. I built high-fidelity Figma prototypes from user personas and empathy maps covering an aerial hero, multi-city navigation across TSC Dubai and its sister developments, an Our DNA sustainability narrative, a media hub and enquiry flows. I wrote the branding guidelines, ran content audits and designed a custom icon set for the design system. The work contributed to a 26% increase in sales.",
    skills: [
      'User Research',
      'UI Design',
      'Design System',
      'Figma',
      'High-Fidelity Prototyping',
      'User Personas & Empathy Mapping',
      'Branding Guidelines',
      'Icon Design',
      'Content Audit',
      'Real Estate Marketing UX',
      'Responsive Web Design',
      'Design QA with Developers',
    ],
  },
  {
    image: '/portfolios/design-portfolio/portfolio-12.webp',
    mobileImageLink: '/portfolios/design-portfolio/portfolio-mob-12.webp',
    title: 'Mercury',
    subtitle: 'Insured Shipping for High-Value Goods',
    alt: 'Mercury Portfolio',
    url: '',
    categories: ['All', 'Light', 'Shipping', 'SaaS', 'AI'],
    description:
      "I designed and built Mercury's platform for insured shipping of jewellery and other high-value goods, where customers are really buying security. I created a product-led hero, Who We Serve segmentation, shipping-technology explainers, a resource centre, release-announcement banners and account entry, using a warm, premium palette that reassures instead of shouts. I delivered the component library and implemented the responsive front end. A logistics service now reads as a security product.",
    skills: [
      'UI/UX Design',
      'Web Design',
      'Design System',
      'Figma',
      'Brand-Led Art Direction',
      'Landing Page Design',
      'Trust & Credibility Design',
      'Responsive Web Design',
      'React',
      'Tailwind CSS',
      'HTML5 / CSS3 / JavaScript',
      'Logistics / Insurtech UX',
      'SaaS Product Design',
    ],
    article: (
      <>
        <img
          src="/portfolios/design-portfolio/mercury/01.webp"
          alt="Mercury Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Mercury moves jewellery and other high-value goods, which means the
          thing being sold is not transit — it is the confidence that a
          two-hundred-thousand-dollar parcel arrives. That reframes almost every
          decision on the page. The homepage opens on a single ring rather than
          a warehouse or a truck, and the headline promises care rather than
          speed: we move goods with care.
        </p>
        <p>
          The three cards beneath it, under the heading Peace of mind, name what
          a customer is actually buying — insured shipping up to $200,000 per
          package, a cloud-native online client platform, and concierge-level
          customer service, described as a human when you need one. Coverage,
          software and people, in that order, because that is the order in which
          this buyer worries about them.
        </p>
        <img
          src="/portfolios/design-portfolio/mercury/02.webp"
          alt="Mercury Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Further down the page the proposition turns technical, over FedEx
          Express imagery: proprietary AI-based risk-management tools that
          monitor a shipping routine and reduce the risk of loss at every step.
          Partnering openly with FedEx does the rest of the credibility work —
          expert solutions, time-definite deliveries, advanced visibility and a
          close carrier relationship — because for a young insurer, borrowing an
          established carrier’s reputation is faster than building one. A single
          customer quote and a resources carousel close the argument before the
          page asks for anything.
        </p>
        <img
          src="/portfolios/design-portfolio/mercury/03.webp"
          alt="Mercury Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Behind the marketing site sits the platform the marketing site is
          selling, and most of the design work lives there. The dashboard opens
          on a named greeting and one dominant action — ship with FedEx, create
          a shipping label — with the rest of the day’s work reduced to small
          entry points: quick quote, cost estimator, safe packaging checklist,
          restricted zip codes. A quick-track table, recently used contacts and
          a news rail fill the edges, so the first screen answers what shall I
          do now rather than presenting a wall of analytics.
        </p>
        <p>
          Six areas carry everything else — Ship, Track, Billing, Address Book,
          Account and Support — with tutorials, support and settings pinned to
          the bottom of the sidebar and the signed-in user below them. Keeping
          the navigation to six items was the constraint that made the rest
          possible: pending shipments, saved quotes, bulk WorldShip uploads,
          scheduled pickups, end-of-day reports, package investigations,
          invoices, credit history, ship-from locations, user management,
          notification templates and support tickets all had to file under one
          of those six without a seventh appearing.
        </p>
        <img
          src="/portfolios/design-portfolio/mercury/04.webp"
          alt="Mercury Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The shipment form is where the insurance premise has to survive
          contact with a real workflow. It splits into shipment locations and
          shipment information, and Insured Value sits directly beside Weight as
          a first-class field rather than an upsell buried at the end — for this
          customer, declared value matters as much as what the parcel weighs.
        </p>
        <p>
          Everything that costs money says so. Saturday delivery, adult and
          direct signature and scheduling a pickup are each marked with a fee,
          and a bar fixed across the bottom keeps estimated shipping and
          insurance, surcharges and the running total visible the whole time the
          form is being filled in, ending in Get Quote beside Create Shipment.
          Nobody has to submit anything to find out what it costs, which matters
          when the insurance component means the price is not guessable.
        </p>
        <img
          src="/portfolios/design-portfolio/mercury/05.webp"
          alt="Mercury Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The palette carries the same argument as the copy. A warm orange,
          #FF6200, over near-black #101828 text and #F2F4F7 surfaces, with a
          teal #40CAD0 held back for small confirmations — rating stars, a
          carousel control, a secondary link. Logistics brands default to
          high-contrast yellows and reds that read as urgency; urgency is the
          wrong feeling for someone posting a diamond ring, so the warmth is
          dialled toward premium and calm instead.
        </p>
        <img
          src="/portfolios/design-portfolio/mercury/06.webp"
          alt="Mercury Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Inter runs the whole system, from fourteen-pixel table labels up to
          the forty-eight-pixel display line, which is what let one component
          library stretch across a marketing site, an authentication and
          four-step onboarding flow, a dense shipping application and a
          printable invoice without any of them looking borrowed from another
          product.
        </p>
        <img
          src="/portfolios/design-portfolio/mercury/07.webp"
          alt="Mercury Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg border border-gray-200"
        />
        <p>
          Read end to end, the site and the platform make the same promise
          twice. A logistics service that would normally be sold on price and
          transit time is presented as a security product, and the application
          behind it keeps that promise by putting coverage, cost and proof in
          front of the customer at every step rather than at the end.
        </p>
      </>
    ),
  },
  {
    image: '/portfolios/design-portfolio/portfolio-14.webp',
    mobileImageLink: '/portfolios/design-portfolio/portfolio-mob-14.webp',
    title: 'ESGLibrary',
    alt: 'ESGLibrary Portfolio',
    url: '',
    categories: ['All', 'Dark', 'AI', 'Sustainability'],
    article: (
      <>
        <img
          src="/portfolios/design-portfolio/esglibrary/01.webp"
          alt="ESGLibrary Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Discover ESGLibrary.com, a dedicated platform for Environmental,
          Social, Governance, and Leadership (ESG+L) insights. Aspiring to be
          the leading online library for sustainability, we offer a wealth of
          resources from historical data to cutting-edge research. Our unique
          focus on integrating 'Leadership' into the ESG framework underscores
          our commitment to promoting responsible leadership as crucial to
          advancing sustainability goals.
        </p>
        <img
          src="/portfolios/design-portfolio/esglibrary/02.webp"
          alt="ESGLibrary Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Explore our diverse content designed to keep you informed on the
          latest trends, research developments, and social media conversations
          surrounding sustainability. Dive deeper into how leadership shapes the
          future of environmental and social governance.
        </p>
        <img
          src="/portfolios/design-portfolio/esglibrary/03.webp"
          alt="ESGLibrary Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Discover the comprehensive sustainability resources at ESGLibrary.com,
          your hub for actionable insights and tools in environmental, social,
          and governance (ESG) initiatives. Our platform features an easy-to-use
          Carbon Calculator that provides access to emission estimations for
          over 12,000 activities, promoting responsible decision-making in
          personal and professional contexts. Dive into our extensive library
          which houses the latest industry news, interviews, technologies, and
          educational resources to help you stay ahead in sustainability
          practices. Explore various sections including the United Nations'
          Sustainable Development Goals (SDGs) to understand and contribute
          towards global efforts in ending poverty, protecting the planet, and
          ensuring prosperity for all.
        </p>
        <img
          src="/portfolios/design-portfolio/esglibrary/04.webp"
          alt="ESGLibrary Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          At ESGLibrary.com, our design philosophy centers on simplicity and
          engagement, reflected in our choice of a dark blue background with
          striking red, yellow, and green accents. These colors are selected not
          just for their aesthetic appeal but to facilitate clarity and focus,
          particularly around our dynamic video previews. The white elements
          maintain a clean and minimalist vibe, ensuring that our content stands
          out and captures user attention effectively.
        </p>
        <img
          src="/portfolios/design-portfolio/esglibrary/05.webp"
          alt="ESGLibrary Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          To complement our visual strategy, we have chosen the 'Poppins' font
          family for its geometric simplicity and modern appeal. This font
          enhances the user experience by offering excellent readability and a
          contemporary feel, aligning with our goal to provide an intuitive and
          visually engaging interface.
        </p>
        <p>
          Our branding choices at ESGLibrary.com are carefully crafted to
          resonate with our audience, ensuring each visit is both pleasing and
          productive.
        </p>
        <img
          src="/portfolios/design-portfolio/esglibrary/06.webp"
          alt="ESGLibrary Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Visit ESGLibrary.com, your premier resource for all things related to
          Environmental, Social, Governance, and Leadership (ESG+L)
          sustainability. Our platform offers a diverse range of content from
          the latest industry news to in-depth podcasts discussing sustainable
          practices and innovations. Whether you're looking to enhance your
          knowledge with our extensive library of articles, watch educational
          videos, or listen to insightful podcasts, ESGLibrary provides valuable
          resources to empower your sustainability journey. Explore our
          categories including governance, responsible leadership, and the
          circular economy to stay informed and inspired.
        </p>
        <img
          src="/portfolios/design-portfolio/esglibrary/07.webp"
          alt="ESGLibrary Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg border border-gray-200"
        />
      </>
    ),
  },
  {
    image: '/portfolios/design-portfolio/portfolio-13.webp',
    mobileImageLink: '/portfolios/design-portfolio/portfolio-mob-13.webp',
    title: 'Islam, Leadership and Sustainability',
    subtitle: 'Editorial Knowledge Platform',
    alt: 'ILS Portfolio',
    url: '',
    categories: ['All', 'Dark', 'AI', 'Sustainability'],
    description:
      'I designed and built Islam, Leadership & Sustainability, a publishing hub connecting Islamic ethics with responsible leadership. I created the Knowledge Hub for articles, a video library, podcasts and a newsletter capture module in a dark, green-accented interface, and delivered a design system tuned for long-form reading in dark mode — contrast, measure and card hierarchy. I implemented the responsive front end. A niche editorial mission gained a real publishing platform instead of a blog theme.',
    skills: [
      'UI/UX Design',
      'Web Design',
      'Design System',
      'Figma',
      'Editorial / Content Design',
      'Dark Mode UI',
      'Typography & Readability',
      'Information Architecture',
      'Responsive Web Design',
      'React',
      'Tailwind CSS',
      'HTML5 / CSS3 / JavaScript',
      'Sustainability Sector Design',
    ],
  },
  {
    image: '/portfolios/design-portfolio/portfolio-15.webp',
    mobileImageLink: '/portfolios/design-portfolio/portfolio-mob-15.webp',
    title: 'Trapix',
    alt: 'Trapix Portfolio',
    url: '',
    categories: ['All', 'Dark', 'Crypto', 'SaaS'],
    article: (
      <>
        <img
          src="/portfolios/design-portfolio/trapix-staking/01.webp"
          alt="Trapix Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Dive into the world of cryptocurrency trading with Trapix Exchange, a
          digital trading platform meticulously crafted to stand out in the
          fintech universe. This case study unfolds the creative process behind
          the platform’s design, showcasing a perfect blend of technology and
          aesthetics tailored for swift and secure trading experiences.
        </p>
        <p>
          Trapix's branding resonates through a vibrant color palette including
          deep black, electric violet, and fiery orange, capturing the dynamic
          essence of the crypto market. These colors facilitate a captivating
          visual hierarchy and reinforce the brand identity across various
          touchpoints.
        </p>
        <img
          src="/portfolios/design-portfolio/trapix-staking/02.webp"
          alt="Trapix Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The typography is thoughtfully chosen for its clarity and modernity. A
          mix of sans-serif fonts reflects digital precision, ensuring excellent
          readability and an engaging content structure.
        </p>
        <img
          src="/portfolios/design-portfolio/trapix-staking/03.webp"
          alt="Trapix Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Embark on a streamlined cryptocurrency trading journey with our
          intuitive platform, designed to simplify the complexities of the
          market. Our user-friendly interface features three distinct
          dashboards: Spot Trading, Wallet, and Future Market, each tailored to
          enhance your trading experience. The Spot Trading screen offers a
          detailed order book, live trading charts, and quick buy/sell options,
          ensuring you stay ahead of market movements. The Wallet dashboard
          provides a clear view of your asset holdings and transaction history,
          making portfolio management straightforward. For advanced traders, the
          Future Market screen displays open interest, long/short ratios, and
          real-time profit and loss, allowing for strategic decision-making.
          Whether you're a novice or a professional, our platform equips you
          with the tools needed to navigate the cryptocurrency markets
          efficiently.
        </p>
        <img
          src="/portfolios/design-portfolio/trapix-staking/04.webp"
          alt="Trapix Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Developed with cutting-edge technology, Trapix Exchange utilizes a
          tech stack that includes HTML5, CSS3, and JavaScript, with React for
          dynamic user interfaces. Integration with APIs for live data and
          secure cloud services ensures a robust and responsive design. Figma
          was instrumental in prototyping and collaborating on the UI/UX design,
          ensuring a seamless workflow from concept to final product.
        </p>
        <img
          src="/portfolios/design-portfolio/trapix-staking/05.webp"
          alt="Trapix Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Key Features:
          <ul>
            <li>Real-time market analytics.</li>
            <li>Multi-asset support.</li>
            <li>Advanced security framework.</li>
          </ul>
        </p>
        <img
          src="/portfolios/design-portfolio/trapix-staking/06.webp"
          alt="Trapix Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg border border-gray-200"
        />
        <p>
          Trapix Exchange embodies a harmonious blend of function and form. This
          design case study exhibits not just a user-centric interface but a
          visual language that communicates trust, innovation, and the
          exhilarating pace of cryptocurrency trading. It stands as a testament
          to a design that empowers and evolves with its users.
        </p>
        <img
          src="/portfolios/design-portfolio/trapix-staking/07.webp"
          alt="Trapix Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg border border-gray-200"
        />
      </>
    ),
  },
  {
    image: '/portfolios/design-portfolio/portfolio-16.webp',
    mobileImageLink: '/portfolios/design-portfolio/portfolio-mob-16.webp',
    title: 'Devigners',
    alt: 'Devigners Portfolio',
    url: 'https://devigners.net/',
    categories: ['All', 'Dark', 'Agency', 'AI'],
    article: (
      <>
        <img
          src="/portfolios/design-portfolio/devigners/01.webp"
          alt="Devigners Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          At Devigners, we create designs that seamlessly integrate with your
          digital environment, whether you're browsing on a website or
          interacting with an app. Our approach to design goes beyond
          aesthetics—we focus on functionality, enhancing your user experience
          with every click. Our philosophy is to build digital solutions that
          are not only visually appealing but also intuitive and lasting.
        </p>
        <p>
          Explore our innovative payment solutions featured on Devigners.net.
          This portfolio highlight showcases our streamlined financial
          management interfaces, supporting multiple payment platforms like
          Apple Pay, Stripe, and PayPal. Designed to enhance project efficiency
          globally, it exemplifies our commitment to providing easy, versatile
          payment options for businesses of all sizes.
        </p>
        <img
          src="/portfolios/design-portfolio/devigners/02.webp"
          alt="Devigners Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Discover ESGLibrary.com, featured in our portfolio at Devigners.net,
          which illustrates our expertise in creating visually engaging and
          functional digital platforms. This website serves as a hub for
          Environmental, Social, Governance, and Leadership (ESG+L)
          sustainability resources, offering users easy access to a wealth of
          information with its intuitive design and user-friendly interface.
          Perfect for those seeking to enhance their knowledge and leadership in
          sustainability, ESGLibrary.com exemplifies our commitment to designing
          solutions that are not only aesthetic but also deeply functional and
          educational.
        </p>
        <img
          src="/portfolios/design-portfolio/devigners/03.webp"
          alt="Devigners Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          At Devigners, our website’s color palette is crafted to encapsulate
          the essence of our brand. It’s not just about aesthetics; it's a
          narrative of our commitment to innovation, precision, and style. Our
          chosen colors include:
          <ul>
            <li>
              🔴 Deep Coral Red: Vibrant and energizing, reflecting our passion
              and drive.
            </li>
            <li>
              🔘 Sleek Anthracite Gray: Modern and professional, symbolizing our
              meticulous attention to detail.
            </li>
            <li>
              ⚫ Timeless Black: Classic and sophisticated, representing our
              enduring excellence.
            </li>
          </ul>
        </p>
        <img
          src="/portfolios/design-portfolio/devigners/04.webp"
          alt="Devigners Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          For our typography, we've selected the 'Inter' font family, renowned
          for its exceptional versatility and readability—crucial for digital
          platforms. Inter is designed to ensure optimal legibility across
          various sizes, ideal for digital screens where clarity is key. With a
          slightly tall x-height, Inter keeps text clear and legible, even at
          smaller sizes, while its neutral yet welcoming tone adapts
          effortlessly to various design contexts.
        </p>
        <p>
          These elements are carefully chosen to enhance user experience and
          reinforce brand identity, ensuring that every interaction with our
          digital presence resonates with our core values.
        </p>
        <img
          src="/portfolios/design-portfolio/devigners/05.webp"
          alt="Devigners Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Visit Devigners.net to experience our expertise in crafting digital
          masterpieces, from concept to canvas. Our website showcases a range of
          services including graphic design, web development, UI/UX design,
          mobile application development, and custom solutions tailored to meet
          diverse industry needs. Each service is designed to enhance
          efficiency, streamline communication, and ensure top-tier quality.
          With our user-friendly interface and robust technical solutions, we
          empower businesses to excel in the digital landscape. Explore our
          latest blog posts for insights on advancing your business, and connect
          with us today to elevate your digital presence.
        </p>
        <img
          src="/portfolios/design-portfolio/devigners/06.webp"
          alt="Devigners Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg border border-gray-200"
        />
      </>
    ),
  },
  {
    image: '/portfolios/design-portfolio/portfolio-17.webp',
    mobileImageLink: '/portfolios/design-portfolio/portfolio-mob-17.webp',
    title: 'Trapix Payment',
    alt: 'Trapix Payment Portfolio',
    url: '',
    categories: ['All', 'Dark', 'Crypto', 'SaaS'],
    article: (
      <>
        <img
          src="/portfolios/design-portfolio/trapix-payment/01.webp"
          alt="Trapix Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Trapix Payment, a leader in financial technology, aimed to
          revolutionize payment processing for businesses globally. The mission
          was clear: develop a user-friendly, intuitive web and mobile platform
          to enable seamless transactions and provide comprehensive financial
          management tools.
        </p>
        <p>
          Color branding was strategically chosen to enhance visual coherence
          and strengthen Trapix Payment’s brand identity. The palette, dominated
          by a professional dark theme, echoes the company's core values of
          trust and innovation, optimizing legibility and user focus.
        </p>
        <img
          src="/portfolios/design-portfolio/trapix-payment/02.webp"
          alt="Trapix Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The typography employs a contemporary sans-serif font, aligning with
          the platform's sleek design to boost readability and streamline the
          user experience. This thoughtful selection supports a frictionless
          journey through the platform, complementing its aesthetic and
          functional goals.
        </p>
        <img
          src="/portfolios/design-portfolio/trapix-payment/03.webp"
          alt="Trapix Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Initiated with a vision of user-centric design, the Trapix Payment
          platform was developed to embody efficiency and innovation. The design
          utilizes a modern, dark theme that reflects professionalism and
          simplifies navigation, making financial management visually accessible
          and straightforward.
        </p>
        <img
          src="/portfolios/design-portfolio/trapix-payment/04.webp"
          alt="Trapix Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The front-end of the Trapix platform was developed using HTML5, CSS3,
          and JavaScript, with React enhancing the dynamic aspects of the
          interface. Figma played a crucial role in creating high-fidelity
          prototypes, ensuring precision in the design-to-development
          transition. The back-end prioritizes security, a critical component
          for handling sensitive financial data.
        </p>
        <img
          src="/portfolios/design-portfolio/trapix-payment/05.webp"
          alt="Trapix Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Key Features:
          <ul>
            <li>
              Responsive design ensuring functionality across all devices.
            </li>
            <li>A real-time dashboard with comprehensive analytics.</li>
            <li>
              Advanced reporting tools to track customer growth and transaction
              data.
            </li>
            <li>High-security payment solutions integrated seamlessly.</li>
            <li>A testimonials section to build trust and credibility.</li>
          </ul>
        </p>
        <img
          src="/portfolios/design-portfolio/trapix-payment/06.webp"
          alt="Trapix Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg border border-gray-200"
        />
        <p>
          The newly launched Trapix Payment platforms have dramatically improved
          client operations, offering scalable, secure payment processing
          solutions for diverse business sizes. With enhanced usability and
          stringent security measures, these platforms stand out in the fintech
          space, empowering clients from startups to large enterprises.
        </p>
      </>
    ),
  },
  {
    image: '/portfolios/design-portfolio/portfolio-18.webp',
    mobileImageLink: '/portfolios/design-portfolio/portfolio-mob-18.webp',
    title: 'Green Care',
    alt: 'Green Care Portfolio',
    url: '',
    categories: ['All', 'Light', 'Sustainability', 'SaaS'],
    article: (
      <>
        <img
          src="/portfolios/design-portfolio/green-care/01.webp"
          alt="Green Care Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Join us on a transformative journey with Green Care, where we merge
          the tranquility of nature with the conveniences of modern living. This
          case study explores our process in creating a digital platform that
          integrates elements of green living into everyday home care,
          cultivating spaces that not only function efficiently but also promote
          well-being.
        </p>
        <p>
          Green Care's color scheme features a harmonious blend of blue and
          light blue themes, embodying the brand's dedication to natural
          well-being and safety. The strategic use of white space enhances the
          sense of cleanliness and openness, providing a user-friendly
          environment that encourages exploration without clutter.
        </p>
        <img
          src="/portfolios/design-portfolio/green-care/02.webp"
          alt="Green Care Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          For typography, we chose a crisp, sans-serif font that embodies
          comfort and reliability. This font choice complements the clean and
          streamlined website design, facilitating easy navigation and
          readability, and reflects Green Care’s commitment to service
          excellence.
        </p>
        <img
          src="/portfolios/design-portfolio/green-care/03.webp"
          alt="Green Care Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The website is built on a robust framework of HTML5, CSS3, and
          JavaScript, which ensures compatibility across various platforms and
          responsiveness to different device screens. We employed React.js for
          its dynamic interface capabilities, while Figma supported a
          collaborative and iterative design process.
        </p>
        <img
          src="/portfolios/design-portfolio/green-care/04.webp"
          alt="Green Care Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Key Features:
          <ul>
            <li>User-friendly service categories.</li>
            <li>Transparent pricing modules.</li>
            <li>Engaging visual elements that draw the user’s eye.</li>
            <li>Streamlined booking and checkout processes.</li>
            <li>An informative and easily accessible 'Contact Us' page.</li>
          </ul>
        </p>
        <img
          src="/portfolios/design-portfolio/green-care/05.webp"
          alt="Green Care Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg border border-gray-200"
        />
        <p>
          Green Care’s website serves as more than just a service portal; it is
          a testament to our expertise in marrying functional design with
          environmental aesthetics. It stands as a pillar of our design
          philosophy, where each element is meticulously crafted to enhance user
          experience while fostering a greener, more sustainable future.
        </p>
      </>
    ),
  },
  {
    image: '/portfolios/design-portfolio/portfolio-19.webp',
    mobileImageLink: '/portfolios/design-portfolio/portfolio-mob-19.webp',
    title: 'FSR AI',
    subtitle: 'Student Council Website, AI M.Sc. at BTU Cottbus-Senftenberg',
    alt: 'FSR AI Portfolio',
    url: 'https://www.fsr-ai.de/',
    categories: ['All', 'Light', 'AI', 'Social'],
    description:
      'I designed and built the website for FSR AI, the student council for the Artificial Intelligence M.Sc. at BTU Cottbus-Senftenberg. I created the programme overview, timetable, Student Guide 2026, upcoming events with sign-ups, BTU quick links, community channels and a Join Office Call entry point. I delivered the blue-on-white identity, a reusable design system and the responsive front-end build. Students now find timetables, guides and events in one place instead of scattered PDFs and chat links.',
    skills: [
      'Web Design',
      'Front-End Development',
      'Design System',
      'Figma',
      'Information Architecture',
      'React',
      'Next.js',
      'Tailwind CSS',
      'Responsive Web Design',
      'Accessibility',
      'Event & Community Platform Design',
      'SEO Fundamentals',
      'Education Sector Design',
    ],
  },
  {
    image: '/portfolios/design-portfolio/portfolio-20.webp',
    mobileImageLink: '/portfolios/design-portfolio/portfolio-mob-20.webp',
    title: 'KRegime',
    subtitle: 'Personalised Korean Skincare E-Commerce',
    alt: 'KRegime Portfolio',
    url: 'https://www.kregime.com/',
    categories: ['All', 'Light', 'eCommerce'],
    description:
      "I designed and built KRegime, an online store selling personalised Korean skincare regime boxes. I created the brand-led hero, guided 3, 5 or 7-step regime selection, a handpicked-products section, brand catalogue, a How It Works explainer, reviews and the cart and checkout flow. I delivered the coral-on-cream identity, the product and selection components, the shop's design system and the responsive front-end implementation. Shoppers now get a personalised routine instead of guessing between products.",
    skills: [
      'Web Design',
      'Front-End Development',
      'Design System',
      'Figma',
      'E-commerce UX',
      'Product Quiz / Guided Selling Flows',
      'Cart & Checkout Design',
      'Brand Identity',
      'React',
      'Tailwind CSS',
      'Responsive Web Design',
      'Conversion Rate Optimisation',
      'Beauty / DTC Brand Design',
    ],
    article: (
      <>
        <img
          src="/portfolios/design-portfolio/kregime/01.webp"
          alt="KRegime Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Korean skincare is sold as products but practised as a routine, and
          that mismatch is where most shoppers give up. A first-time buyer
          doesn’t need another shelf of serums to compare; they need to know
          what to use, in what order, for their skin. KRegime sells the routine
          itself — a curated box of three, five or seven steps — so the decision
          moves from which product to how far you want to go.
        </p>
        <p>
          The homepage commits to that from the first screen. A macro
          photograph, four words, and one primary action: find your regime.
          Nothing is priced, compared or explained above the fold, because the
          product being sold is a recommendation rather than an item.
        </p>
        <p>
          What follows builds the credibility that a curation business needs.
          Four plain promises — hand-picked by experts, premium Korean brands,
          visible results, free shipping and returns — are followed by a wall of
          twenty-five brand marks, from COSRX and Anua to Beauty of Joseon,
          innisfree and TIRTIR. A Transformation Journey timeline then sets
          honest expectations against real intervals: brighter complexion at
          seven days, clearer skin at fourteen, visible reduction at thirty, and
          healthy skin at three months. Naming the slow parts is what makes the
          fast ones believable.
        </p>
        <img
          src="/portfolios/design-portfolio/kregime/02.webp"
          alt="KRegime Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The regime selector is the commercial heart of the shop. TRIBOX,
          PENTABOX and SEPTABOX map directly onto three, five and seven steps,
          with PENTABOX badged Most Popular to give the undecided a default. A
          toggle above the cards switches between a one-time purchase and three-
          or six-month subscriptions, repricing all three tiers in place rather
          than sending anyone to a separate page.
        </p>
        <p>
          Each card carries the same anatomy — the box itself, a summary of who
          the routine suits, the suggested steps listed out, then Select This
          Regime as the primary action with Send as a Gift beneath it. Spelling
          out the steps is what makes the tiers legible: TRIBOX is cleanser,
          moisturiser and sunscreen; PENTABOX adds a toner and a serum; SEPTABOX
          adds a cleansing oil and a mask on top of those. A shopper can see
          exactly what the extra steps buy instead of inferring it from a
          number, and the routine is visible before any money or personal detail
          is asked for. Gifting sits on the card rather than buried in checkout,
          because a curated box is an obvious present and asking later would
          mean asking too late.
        </p>
        <img
          src="/portfolios/design-portfolio/kregime/03.webp"
          alt="KRegime Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          Choosing a box hands off to the part that makes the box personal. The
          quiz opens as Customize Your PENTABOX and asks one question per screen
          — age range first, offered as six large targets rather than a
          dropdown. A progress bar reads Step 1 of 8 alongside a percentage
          complete, which is the single most important element on the page: an
          eight-question form asked all at once reads as work, while the same
          eight questions asked one at a time with the end in sight read as a
          consultation.
        </p>
        <img
          src="/portfolios/design-portfolio/kregime/04.webp"
          alt="KRegime Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          How It Works then restates the whole model in three steps on a full
          coral band — choose your regime, complete the quiz, receive and glow —
          placed late on purpose, for the reader who scrolled past the detail
          and needs the shape of the thing before committing.
        </p>
        <p>
          Reviews close the page. Four cards sit at deliberately staggered
          heights so the block reads as a collected wall rather than a tidy row,
          each with a star rating, a named customer and a Verified Customer
          badge. The footer carries the newsletter as Join the Glow Community,
          which frames a mailing list as membership.
        </p>
        <img
          src="/portfolios/design-portfolio/kregime/05.webp"
          alt="KRegime Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          The identity is a single coral, #EF7E71, over a warm cream of #F7F2F0,
          with #BF655A deepening the footer bands and #212121 carrying the text.
          The restraint is the point: in a category that defaults to pastel
          gradients and heavy decoration, one accent doing all the work — every
          button, every highlighted second word in a heading, every step marker
          — reads as considered rather than loud, and lets the product
          photography supply the colour.
        </p>
        <img
          src="/portfolios/design-portfolio/kregime/06.webp"
          alt="KRegime Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg"
        />
        <p>
          DM Sans runs from fourteen-pixel labels to the sixty-pixel hero, its
          geometric, slightly rounded forms sitting closer to a beauty brand
          than a standard interface face while staying quiet enough for
          ingredient lists and quiz options. The headings use a consistent
          device throughout — a neutral phrase with the operative word flipped
          to coral, The Handpicked Regime, Choose Your Regime, What Our
          Customers Say — which gives every section the same rhythm without
          needing a second typeface.
        </p>
        <img
          src="/portfolios/design-portfolio/kregime/07.webp"
          alt="KRegime Portfolio"
          width={600}
          height={400}
          className="w-full object-cover rounded-lg border border-gray-200"
        />
        <p>
          Read end to end, the page is one funnel with no branches: a promise,
          the proof behind it, three ways to commit, and a quiz that turns the
          purchase into a personal recommendation. Shoppers stop guessing
          between products and start a routine chosen for their skin.
        </p>
      </>
    ),
  },
];
